import React, { type JSX } from 'react';

import { preferencesV2Selectors } from 'app-components/flux/preferences-v2';
import { useAppSelector } from 'hooks/redux';

import AppBar from '@mui/material/AppBar';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import GuideButton from 'components/GuideButton';
import SimplifiAndQBPLogo from 'components/SimplifiAndQBPLogo';
import { makeStyles } from 'tss-react/mui';
import { useNavOverlay } from 'containers/App/Header/NavExpansionContext';
import { navBarWidth, navToggleTransition } from 'containers/App/Header/commonNavStyles';
import { navIconMenu as NavMenuIcon } from 'containers/App/navIcons';
import TopBarNavItems from './TopBarNavItems';

const useStyles = makeStyles<{ hideNavButtons: boolean }>()((theme, { hideNavButtons }) => ({
  appBar: {
    display: 'flex',
    position: 'sticky',
    top: 0,
    maxWidth: `calc(100vw - var(--topbar-left-offset, ${navBarWidth}px))`,
    transition: theme.transitions.create('max-width', navToggleTransition(theme)),
    zIndex: hideNavButtons ? theme.zIndex.modal : theme.zIndex.appBar,
  },
  toolbar: {
    justifyContent: 'space-between',
    gap: theme.spacing(1),
  },
  toolbarWithNavMenu: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(3),
  },
  titleBlock: {
    minWidth: 0,
  },
  titleArea: {
    minWidth: 0,
    flexShrink: 1,
  },
  navMenuButton: {
    flex: 'none',
    color: theme.palette.text.primary,
    '&:hover, &:focus-visible': {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
  },
  tabsRow: {
    justifyContent: 'space-between',
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: theme.spacing(6),
    paddingRight: theme.spacing(3),
  },
  tabsContainer: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
}));

export type TopBarProps = {
  /** Main heading text. Ignored when logo is set. */
  title?: string;
  /** DOM id for the title element (e.g. for Cypress). */
  titleId?: string;
  /** When true, shows the SimplifiAndQBPLogo . When a node, shows that instead of the title. */
  logo?: React.ReactNode | boolean;
  /** When set, shows the useGuideButton next to the title or logo. */
  guideId?: string;
  /** Content shown on the right side of the toolbar row. */
  actionArea?: React.ReactNode;
  /** Content shown on the right side of the secondary row. */
  tabsActionArea?: React.ReactNode;
  /** Optional content in a second row below the toolbar (e.g. tab navigation). */
  tabs?: React.ReactNode;
  /** DOM id for the bar; default 'page-header'. */
  id?: string;
};

const TopBar = ({
  title,
  titleId,
  logo,
  guideId,
  actionArea,
  tabsActionArea,
  tabs,
  id = 'page-header',
}: TopBarProps): JSX.Element => {
  const hideNavButtons = useAppSelector(
    (state) => preferencesV2Selectors.getPreferenceValue(state, 'gsfScreen', 'completed_gsf') !== 'completed_gsf',
  );
  const { classes, cx } = useStyles({ hideNavButtons });
  const theme = useTheme();
  const isSmallWindow = useMediaQuery(theme.breakpoints.down('md'));
  const navOverlay = useNavOverlay();
  const showNavMenuButton = Boolean(navOverlay?.compact) && !hideNavButtons;

  const titleOrLogo = (() => {
    if (logo === true) return <SimplifiAndQBPLogo showLogo />;
    if (logo != null && logo !== false) return logo as React.ReactNode;
    if (hideNavButtons) return <SimplifiAndQBPLogo />;
    if (title != null) {
      return (
        <Stack
          direction='row'
          gap={1}
          id={titleId ?? 'page-main-title'}
          alignItems='center'
          className={classes.titleBlock}
        >
          <Typography
            variant={isSmallWindow ? 'titleMedium' : 'headlineMedium'}
            noWrap
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {title}
          </Typography>
        </Stack>
      );
    }
    return null;
  })();

  const hasSecondaryRow = tabs != null || tabsActionArea != null;

  return (
    <AppBar
      id={id}
      className={classes.appBar}
      position='sticky'
      color='inherit'
      elevation={12}
      sharedcomponentid='TopBar'
    >
      <Toolbar
        className={cx(classes.toolbar, showNavMenuButton && classes.toolbarWithNavMenu)}
        disableGutters={showNavMenuButton}
      >
        <Stack direction='row' gap={1} alignItems='center' className={classes.titleArea}>
          {showNavMenuButton ? (
            <IconButton
              size='medium'
              shape='rounded'
              aria-label='Open navigation'
              aria-expanded={navOverlay?.overlayOpen}
              aria-controls='application-header-bar'
              onClick={() => navOverlay?.openOverlay('menu-button')}
              className={classes.navMenuButton}
            >
              <NavMenuIcon aria-hidden width={20} height={20} />
            </IconButton>
          ) : null}
          {titleOrLogo}
          {guideId != null ? <GuideButton guideId={guideId} /> : null}
        </Stack>
        {actionArea ?? null}
        <TopBarNavItems />
      </Toolbar>
      {hasSecondaryRow ? <Divider /> : null}
      {hasSecondaryRow ? (
        <Stack className={classes.tabsRow}>
          <div className={classes.tabsContainer}>{tabs}</div>
          {tabsActionArea ?? null}
        </Stack>
      ) : null}
    </AppBar>
  );
};

export default TopBar;
